import { testReducedMotion, toArray, toggleAttribute } from '@nrk/dh-basis-common'

const STATE_ATTR = 'data-state'
const VIEWPORT_POSITION_ATTR = 'data-viewport-position'
const DISABLE_FORCED_ATTRIBUTES_ATTR = 'disable-forced-attributes'
const IS_PAUSED = 'is-paused'
const AUTOPLAY_BLOCKED = 'autoplay-blocked'

const isVisible = child => child.offsetWidth > 0 || child.offsetHeight > 0

export default class LevendeStillbilde extends HTMLElement {
  static get observedAttributes() {
    return [STATE_ATTR, DISABLE_FORCED_ATTRIBUTES_ATTR, VIEWPORT_POSITION_ATTR]
  }

  _disconnecting = false

  connectedCallback() {
    this._setVideoAttributes()
    this._setEventListeners()
    this._updatePlayState()
  }

  disconnectedCallback() {
    this._disconnecting = true
    this._removeEventListeners()
  }

  attributeChangedCallback(name, prevValue, newValue) {
    const reducedMotion = testReducedMotion()
    const valueChanged = prevValue !== newValue
    if ((name === STATE_ATTR || name === VIEWPORT_POSITION_ATTR) && !reducedMotion) {
      if (valueChanged) {
        this._updatePlayState()
        if (name === VIEWPORT_POSITION_ATTR && newValue === 'IN-VIEWPORT') {
          this._preload()
        }
      }
    }
  }

  _setEventListeners() {
    window.addEventListener('resize', this._handleResize)
    this.videoElements.forEach(el => {
      el.addEventListener('pause', this._handlePlayChange)
      el.addEventListener('play', this._handlePlayChange)
      el.addEventListener('loadedmetadata', this._updatePlayState)
    })
  }

  _removeEventListeners() {
    window.removeEventListener('resize', this._handleResize)
    this.videoElements.forEach(el => {
      el.removeEventListener('pause', this._handlePlayChange)
      el.removeEventListener('play', this._handlePlayChange)
      el.removeEventListener('loadedmetadata', this._updatePlayState)
    })
  }

  _handlePlayChange = () => {
    this.isPaused = this.visibleVideoElement.paused
  }

  _setVideoAttributes() {
    if (!this.disableForcedAttributes) {
      this.videoElements.forEach(el => {
        el.setAttribute('playsInline', '')
        el.setAttribute('loop', '')
        el.setAttribute('disableRemotePlayback', '')
        el.setAttribute('x-webkit-airplay', 'deny')
      })
    }
  }

  _updatePlayState = () => {
    if (!this.visibleVideoElement) {
      return
    }

    if (this._shouldPlay()) {
      this._playVideo()
    } else {
      this._pauseVideo()
    }
  }

  _handleResize = () => {
    this._updatePlayState()
    this._preload()
  }

  _shouldPlay() {
    return (
      this.visibleVideoElement.readyState >= 1 &&
      this.state === 'ACTIVE' &&
      this.viewportPosition === 'IN-VIEWPORT'
    )
  }

  _preload() {
    if (this.visibleVideoElement.getAttribute('preload') !== 'auto') {
      this.visibleVideoElement.setAttribute('preload', 'auto')
    }
  }

  _playVideo() {
    const videoElement = this.visibleVideoElement
    if (videoElement.paused && !this.autoplayBlocked) {
      const playPromise = videoElement.play() || Promise.resolve()
      playPromise.catch(e => {
        console.error(e)
        this.autoplayBlocked = true
      })
    }
  }

  _pauseVideo() {
    const videoElement = this.visibleVideoElement
    if (!videoElement.paused) {
      videoElement.pause()
    }
  }

  get visibleVideoElement() {
    return this.videoElements.find(isVisible)
  }

  get videoElements() {
    return toArray(this.querySelectorAll('video'))
  }

  get disableForcedAttributes() {
    return this.hasAttribute(DISABLE_FORCED_ATTRIBUTES_ATTR)
  }

  get state() {
    return this.getAttribute(STATE_ATTR)
  }

  set state(val) {
    this.setAttribute(STATE_ATTR, val)
  }

  get isPaused() {
    return this.getAttribute(IS_PAUSED)
  }

  set isPaused(val) {
    if (val) {
      this.setAttribute(IS_PAUSED, '')
    } else {
      this.removeAttribute(IS_PAUSED)
    }
  }

  get autoplayBlocked() {
    return this.hasAttribute(AUTOPLAY_BLOCKED) || false
  }

  set autoplayBlocked(val) {
    toggleAttribute(this, AUTOPLAY_BLOCKED, val)
  }

  get viewportPosition() {
    return this.getAttribute(VIEWPORT_POSITION_ATTR)
  }

  set viewportPosition(val) {
    this.setAttribute(VIEWPORT_POSITION_ATTR, val)
  }
}
